import { parseCoverDoc, type CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { leadTokenOf } from './publicCoverPath';

/** The design of the public tool in this browser (spec §2): the document and the two fields. */
export const PUBLIC_COVER_DOC_KEY = 'm24CoverMakerDoc';
/** The lead token of this browser: the visitor left an email once (owner decision D12). */
export const PUBLIC_COVER_LEAD_KEY = 'm24CoverMakerLead';
/** A design that waits for a sign-in: a lead token, or `local` for the design of this browser. */
export const PUBLIC_COVER_CARRY_KEY = 'm24CoverMakerCarry';
/** A lead that the API did not take (it was down): the next download sends it again. */
export const PUBLIC_COVER_PENDING_EMAIL_KEY = 'm24CoverMakerPendingEmail';

/** The email of the lead of this browser: a later download updates the saved design with it. */
export const PUBLIC_COVER_EMAIL_KEY = 'm24CoverMakerEmail';

export const LOCAL_CARRY = 'local';
const FIELD_MAX = 200;

export interface PublicCover {
  doc: CoverDoc;
  title: string;
  artist: string;
}

/** The part of `Storage` that the public tool uses. Tests give a map. */
export type CoverStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The storage of the browser, or null where it is blocked (a private window can throw). */
export function browserCoverStorage(): CoverStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function read(storage: CoverStorage | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(storage: CoverStorage | null, key: string, value: string | null): void {
  try {
    if (value === null) {
      storage?.removeItem(key);
    } else {
      storage?.setItem(key, value);
    }
  } catch {
    // A full or blocked storage: the design stays in memory for this visit.
  }
}

const fieldOf = (value: unknown): string => (typeof value === 'string' ? value.slice(0, FIELD_MAX) : '');

/**
 * The saved design of this browser, checked with `parseCoverDoc`. Null with no design, or with a
 * value that does not parse (an older shape, a changed value): the tool then starts new. Pure.
 */
export function readPublicCover(storage: CoverStorage | null): PublicCover | null {
  const raw = read(storage, PUBLIC_COVER_DOC_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const value = JSON.parse(raw) as { doc?: unknown; title?: unknown; artist?: unknown } | null;
    const doc = parseCoverDoc(value?.doc);
    return doc === null ? null : { doc, title: fieldOf(value?.title), artist: fieldOf(value?.artist) };
  } catch {
    return null;
  }
}

export function writePublicCover(storage: CoverStorage | null, cover: PublicCover): void {
  write(storage, PUBLIC_COVER_DOC_KEY, JSON.stringify(cover));
}

/** The lead token of this browser, or null: the email is asked one time for each browser. */
export function readLeadToken(storage: CoverStorage | null): string | null {
  return leadTokenOf(read(storage, PUBLIC_COVER_LEAD_KEY));
}

export function writeLeadToken(storage: CoverStorage | null, token: string): void {
  write(storage, PUBLIC_COVER_LEAD_KEY, token);
}

/** The carried design: a lead token, `local`, or null. */
export function readCarry(storage: CoverStorage | null): string | null {
  const value = read(storage, PUBLIC_COVER_CARRY_KEY);
  return value === LOCAL_CARRY ? LOCAL_CARRY : leadTokenOf(value);
}

export function writeCarry(storage: CoverStorage | null, value: string | null): void {
  write(storage, PUBLIC_COVER_CARRY_KEY, value);
}

export function readPendingEmail(storage: CoverStorage | null): string | null {
  return read(storage, PUBLIC_COVER_PENDING_EMAIL_KEY);
}

export function writePendingEmail(storage: CoverStorage | null, email: string | null): void {
  write(storage, PUBLIC_COVER_PENDING_EMAIL_KEY, email);
}

export function readLeadEmail(storage: CoverStorage | null): string | null {
  return read(storage, PUBLIC_COVER_EMAIL_KEY);
}

export function writeLeadEmail(storage: CoverStorage | null, email: string | null): void {
  write(storage, PUBLIC_COVER_EMAIL_KEY, email);
}
